import {
  REAUTHORIZE_INTEGRATION_ERROR_CODES,
  type IntegrationConnectionStatus,
  type IntegrationErrorCode,
} from "@playbookai/api-clients";

/**
 * SHELL-OWNED, deliberately (PB-4734, G5 review).
 *
 * This lived in `@playbookai/api-clients` for one release and should not have:
 * it is a PRODUCT decision about which states deserve a Reconnect affordance,
 * not a fact about the wire contract. The package publishes the server's
 * vocabulary — `IntegrationConnectionStatus`, `IntegrationErrorCode` and the
 * `REAUTHORIZE_INTEGRATION_ERROR_CODES` set it imports below — and the reuse
 * rule is that shared code moves to `packages/` when a SECOND app needs it.
 * The shell is the only consumer, so it stays here until one appears.
 */

/**
 * Whether connecting again is the fix (PB-4734, G5).
 *
 * `REAUTHORIZE_INTEGRATION_ERROR_CODES` was exported and had no caller, so
 * `expired` and `failed`
 * connections rendered a bare badge and the one action that resolves them was
 * offered nowhere. Two facts can say "reconnect", and both route here so the
 * console cannot answer them differently:
 *
 *  - the STORED status, which is what the list shows before anything is tried;
 *  - the sanitised code from a check or a failed action, which is the same
 *    conclusion reached the hard way.
 *
 * `revoked` is deliberately absent. That state is a deliberate disconnect, and
 * a reconnect button on the row that records it reads as undo — the user should
 * go back through the connect flow, which is a different, considered act.
 * `pending` is absent too: an authorization already in flight is not fixed by
 * starting a second one.
 */
export function offersReauthorize(connection: {
  readonly status: IntegrationConnectionStatus;
  readonly lastErrorCode?: IntegrationErrorCode | null | undefined;
}): boolean {
  // The two EXCLUSIONS are checked first, and that ordering is the fix rather
  // than a tidy-up. Written the other way round, the paragraph above described
  // an intent the code did not have: a `revoked` connection carrying
  // `CREDENTIAL_REJECTED` from its last check fell through to the code test and
  // came back `true`, offering a reconnect on the row that records a deliberate
  // disconnect. The status is the stronger fact — it says what the connection
  // IS, where the code only says how it last failed.
  if (connection.status === "revoked" || connection.status === "pending") {
    return false;
  }
  if (connection.status === "expired" || connection.status === "failed") {
    return true;
  }
  return (
    connection.lastErrorCode != null &&
    REAUTHORIZE_INTEGRATION_ERROR_CODES.has(connection.lastErrorCode)
  );
}
